import { useMemo } from 'react';
import SectionSubtitle from '../../Typography/SectionSubtitle';
import SectionTitle from '../../Typography/SectionTitle';
import { CardRating } from '../../CardRating/CardRating';
import { RateAnswerSummary } from './RateAnswer';
import RateAnswer from './RateAnswer/RateAnswer';
import {
  CardSummaryBox,
  CardSummaryBoxProps,
} from '../../CardSummaryBox/CardSummaryBox';
import { SectionNoData } from '../../SectionNoData';
import { getInspectionSummaryBackgroundColor } from '@/helpers/ratingState';
import type { AvailableSection } from '@/api/mappers/public-report.mapper';

export const RAPORT_SHORTCUT_TITLE = 'Skrót raportu';
export const RAPORT_SHORTCUT_DESCRIPTION =
  'Potrzebujesz podjąć szybką decyzję? Nasze streszczenie raportu pozwoli Ci zapoznać się z najważniejszymi informacjami w pigułce.';

const TARGET_ID_TO_SECTION: Record<string, AvailableSection> = {
  'car-body': 'EXTERNAL_INSPECTION',
  'car-interior': 'INTERNAL_INSPECTION',
  'engine-trunk': 'ENGINE_AND_TRUNK_COMPARTMENT',
  'test-drive': 'TEST_DRIVE',
  chassis: 'CAR_CHASSIS',
  'computer-diagnostics': 'COMPUTER_DIAGNOSTICS',
};

export interface RaportShortcutSummaryProps {
  summaryCards: CardSummaryBoxProps[] | null;
  overallRating: number | null;
  expertWouldBuy: boolean | null;
  inspectionSummary: string | null;
  mileageVerificationDescription: string;
  mileageVerificationIsPositive: boolean | null;
  advertisementVerificationDescription: string;
  advertisementVerificationIsPositive: boolean | null;
  sellerInterviewDescription: string;
  availableSections: Set<AvailableSection>;
  /**
   * Czy podsumowanie ma nachodzić (ujemny margines) na poprzedzający je wysoki,
   * ciemny nagłówek raportu (`RaportHeader` w `raport-wrapper`). Domyślnie `true`.
   * W widoku wstępnym ("Wstępne wyniki ekspertyzy"), gdzie nad sekcją jest tylko
   * niski `RaportHeroSection`, należy przekazać `false` — inaczej karta "Ocena
   * pojazdu" nachodzi na tytuł hero.
   */
  overlapHeader?: boolean;
}

export const RaportShortcutSummary = ({
  summaryCards = [],
  overallRating = 0,
  expertWouldBuy = true,
  inspectionSummary,
  mileageVerificationDescription,
  mileageVerificationIsPositive = true,
  advertisementVerificationDescription,
  advertisementVerificationIsPositive = true,
  sellerInterviewDescription,
  availableSections,
  overlapHeader = true,
}: RaportShortcutSummaryProps) => {
  const cardsToDisplay = useMemo(
    () =>
      summaryCards?.filter((card) => {
        if (!card.targetId) return true;
        const section = TARGET_ID_TO_SECTION[card.targetId];
        return !section || availableSections.has(section);
      }) ?? [],
    [summaryCards, availableSections],
  );

  const inspectionSummaryBackgroundColor = useMemo(
    () => getInspectionSummaryBackgroundColor(overallRating),
    [overallRating],
  );

  return (
    <div className={``}>
      <div
        className={`w-full ${overlapHeader ? 'mt-[-450px] lg:mt-[-500px]' : ''}`}
      >
        {/*
         * Tytuł renderujemy wewnętrznie tylko w trybie nachodzenia na ciemny
         * nagłówek raportu (ujemny margines). W widokach bez `RaportHeader`
         * ("Raport w przygotowaniu" / wstępne wyniki) tytuł
         * renderuje pełnoszerokościowo `LayoutSection` — inaczej baner zostałby ucięty
         * do `max-w-[1268px]` kontenera treści, niespójnie z resztą nagłówków sekcji.
         */}
        {overlapHeader && (
          <div className="b-7 relative flex flex-col items-start justify-start gap-2.5 pb-[35px] pt-[30px] lg:ml-[-48px]">
            <SectionTitle
              title={RAPORT_SHORTCUT_TITLE}
              description={RAPORT_SHORTCUT_DESCRIPTION}
              icon={'paper'}
              bgWhite={false}
            />
          </div>
        )}
        <div className="w-[calc(100% - 21px)] flex flex-col rounded-[20px] border-grayBorder bg-white px-0 pt-[28px] lg:ml-[21px] lg:border">
          {overallRating !== null ? (
            <div className="flex flex-col lg:flex-row lg:pr-[17px]">
              <div
                className={`relative z-[2] flex items-center gap-2.5 rounded-3xl bg-white px-5 py-6 shadow-2xl lg:left-[-21px] ${overlapHeader ? 'lg:top-[-54px]' : ''}`}
              >
                <CardRating
                  rating={overallRating}
                  expertWouldBuy={expertWouldBuy}
                />
              </div>
              <div className="mt-12 flex w-full min-w-0 flex-col items-start justify-start gap-[18px] lg:ml-[40px] lg:mt-0">
                <div className="relative flex flex-shrink-0 flex-grow-0 items-center justify-start gap-2.5 self-stretch">
                  <p className="flex-shrink-0 flex-grow-0 text-left text-xl font-bold text-[#20262a]">
                    Podsumowanie oględzin
                  </p>
                </div>
                <div
                  className="relative flex flex-shrink-0 flex-grow-0 items-center justify-center gap-2.5 self-stretch overflow-hidden break-words rounded-[15px] px-7 pb-9 pt-6 lg:max-w-[840px]"
                  style={{ backgroundColor: inspectionSummaryBackgroundColor }}
                >
                  <p className="min-w-0 flex-grow break-words text-left text-[15px] text-[#4b5d53]">
                    {inspectionSummary ||
                      'Podsumowanie oględzin jest w trakcie przygotowania.'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-5 py-6">
              <SectionNoData
                title="Ocena pojazdu w przygotowaniu"
                description="Rzeczoznawca pracuje nad oceną pojazdu. Ocena będzie dostępna wkrótce."
              />
            </div>
          )}

          <div className="flex flex-col items-start justify-start gap-2.5 self-stretch lg:flex-row lg:px-10">
            {mileageVerificationIsPositive !== null && (
              <div className="mt-12 flex-1 lg:mt-0 lg:max-w-[50%]">
                <RateAnswer
                  question={
                    'Czy oględziny pojazdu potwierdzają wskazany przebieg?'
                  }
                  description={mileageVerificationDescription}
                  justificationLabel="Uzasadnienie:"
                  isPositive={mileageVerificationIsPositive}
                />
              </div>
            )}
            {advertisementVerificationIsPositive !== null && (
              <div className="mt-12 flex-1 lg:mt-0 lg:max-w-[50%]">
                <RateAnswer
                  question={
                    'Czy oględziny potwierdzają informacje z ogłoszenia?'
                  }
                  description={advertisementVerificationDescription}
                  justificationLabel="Uzasadnienie:"
                  isPositive={advertisementVerificationIsPositive}
                />
              </div>
            )}
          </div>

          <div className="mt-8 border-grayBorder lg:border-t lg:px-10">
            <RateAnswerSummary
              title={'Wnioski z rozmowy ze sprzedającym'}
              description={sellerInterviewDescription}
            />
          </div>
        </div>
        <div className="-mx-8 mt-10 border-t border-grayBorder pt-10 lg:mt-20 lg:border-t-0 lg:pt-0"></div>
        <div className="lg:mt-20">
          <SectionSubtitle
            title="Podsumowanie najważniejszych sekcji pojazdu"
            description="Prezentowane elementy stanowią podsumowanie poszczególnych części raportu sprawdzenia pojazdu."
          />

          <div className="container mx-0 mt-11 grid grid-cols-1 gap-x-7 gap-y-9 px-0 lg:grid-cols-2">
            {cardsToDisplay &&
              cardsToDisplay.map((card, index) => (
                <CardSummaryBox key={index} {...card} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
